import { TOKEN_LOCALSTORAGE_KEY, clearStoredTokens, updateStoredTokens } from "../hooks/use-auth-tokens.js";
import { decodeJwt } from "jose";

//#region src/client/util/tokens.ts
const FIVE_MINUTES = 300 * 1e3;
const SIXTY_SECONDS = 60 * 1e3;
const THIRTY_SECONDS = 30 * 1e3;
const fetchAuthToken = async (code, clientId, tokenUrl, codeVerifier, redirectUri) => {
	const formData = new URLSearchParams();
	formData.append("client_id", clientId);
	formData.append("code", code);
	formData.append("grant_type", "authorization_code");
	formData.append("redirect_uri", redirectUri);
	formData.append("code_verifier", codeVerifier);
	return fetch(tokenUrl, {
		method: "POST",
		headers: { "content-type": "application/x-www-form-urlencoded" },
		body: formData.toString()
	});
};
const fetchRefreshToken = async (code, clientId, tokenUrl) => {
	const formData = new URLSearchParams();
	formData.append("client_id", clientId);
	formData.append("grant_type", "refresh_token");
	formData.append("refresh_token", code);
	return fetch(tokenUrl, {
		method: "POST",
		headers: { "content-type": "application/x-www-form-urlencoded" },
		body: formData.toString()
	});
};
/**
* Active refresh promise tracker for deduplication.
* Prevents multiple concurrent token refresh operations from executing simultaneously.
* When multiple callers request a refresh, they all wait for the same promise to resolve.
*
* @see MYPT-356 for details on refresh deduplication strategy
*/
let activeRefreshPromise = null;
/**
* Performs a token refresh operation with automatic deduplication.
* If a refresh is already in progress, returns the existing promise instead
* of initiating a new refresh request.
*
* @param refreshToken - The refresh token to use
* @param clientId - OAuth client ID
* @param tokenUrl - Token endpoint URL
* @returns Promise that resolves when refresh completes
* @throws Error if refresh fails or tokens are invalid
*/
const performRefresh = async (refreshToken, clientId, tokenUrl) => {
	if (activeRefreshPromise) return activeRefreshPromise;
	activeRefreshPromise = (async () => {
		try {
			const response = await fetchRefreshToken(refreshToken, clientId, tokenUrl);
			if (!response.ok) throw new Error("An error occurred while trying to refresh the auth token");
			const jwt = await response.json();
			if (isValidTokenResponse(jwt)) updateStoredTokens({
				accessToken: jwt.access_token,
				refreshToken: jwt.refresh_token
			});
			else throw new Error("access token and/or refresh token missing from response");
		} finally {
			activeRefreshPromise = null;
		}
	})();
	return activeRefreshPromise;
};
const isValidTokenResponse = (jwt) => {
	if ("error" in jwt) {
		let err = jwt.error;
		if (jwt.error_description) err += ` -- ${jwt.error_description}`;
		throw new Error(err);
	}
	return !!(jwt.access_token && jwt.refresh_token);
};
const getOrRefreshToken = async (clientId, tokenUrl) => {
	const storedData = localStorage.getItem(TOKEN_LOCALSTORAGE_KEY);
	if (!storedData || !clientId || !tokenUrl) return null;
	try {
		const tokens = JSON.parse(storedData);
		const expiry = tokens.expMs;
		if (!tokens.accessToken) throw new Error("this should not be possible");
		const date = /* @__PURE__ */ new Date();
		date.setMinutes(date.getMinutes() + 1);
		if (expiry > date.getTime()) return tokens.accessToken.raw;
		await performRefresh(tokens.refreshToken, clientId, tokenUrl);
		return getOrRefreshToken(clientId, tokenUrl);
	} catch {
		clearStoredTokens();
	}
	return null;
};
const decodeToken = (token) => {
	try {
		return decodeJwt(token);
	} catch (ex) {
		console.error("something went wrong while decoding the token", ex);
	}
	return null;
};

//#endregion
export { FIVE_MINUTES, SIXTY_SECONDS, THIRTY_SECONDS, decodeToken, fetchAuthToken, getOrRefreshToken, performRefresh };